import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useApolloClient } from '@apollo/client';
import InnerHTML from 'dangerously-set-html-content';
import { python as pythonLanguage } from '@codemirror/lang-python';

import { CodeMirror, lineNumbers, tabIndentExtension } from '@/components/codemirror';
import { useAccountContext } from '@/lib/accounts/context';
import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { Loader } from '@/components/loader';
import { ErrorBoundary, GenericFallback } from '@/lib/error';
import { useLoadingStatus } from '@/lib/hooks';
import { setCellPipeline, setCellTitle, setViewOptions } from '@/core/cell';
import { useTrackEvent } from '@/lib/analytics';
import { HideInEmbedded } from '@/components/layout/hide-in-embedded';
import { setFieldIndex, sortProperties } from '@/explore/utils/field-ordering';
import { shouldPropagateEvent } from '@/explore/utils/interactable';

import { Model, Sort, SortItem, PythonCell } from '../../types';
import { PaginatedRecords } from '../../components/paginated-records';
import { HorizontalScrollTable } from '../../components/horizontal-scroll-table';
import { MasterBadge } from '../../components/master-badge';
import { DataTableProperty, DataTableRow } from '../../components/datatable';
import { CollapseButton, CollapsibleContainer, CollapsibleContent } from '../collapsible-cell';
import { CellTitle } from '../cell-title';
import { CellControls } from '../cell-controls';
import { useExplorationContext } from '../exploration-context';
import { useExplorationCellContext } from '../exploration-cell-context';
import {
  getPythonOperation,
  findDataSource,
  loadDataByDataSource,
  parseCustomPackages,
  setPythonOperation,
} from './utils';
import { pyodideService } from './pyodide-service';
import { selectPythonCellOutput } from './output-selection';
import { getNumberOfChildPipelines } from '../../pipeline/utils';
import { PythonBlockResult, PythonLog, TableResult } from './types';
import { convertPythonResult } from './converters';
import { useMetadataContext } from '../../metadata-context';

import container from '../exploration.module.scss';
import style from './pythoncell.module.scss';
import editorStyle from '../code-editor.module.scss';

interface PythonCellViewProps {
  cell: PythonCell;
  onSelectCell?: () => void;
  onSetDraggable: (value: boolean) => void;
}

export const PythonCellView = (props: PythonCellViewProps) => {
  const { cell } = props;
  const [isDragHovered, setIsDragHovered] = useState(false);

  const { setCell, isCollapsible, isConversationPart } = useExplorationCellContext();
  const { exploration } = useExplorationContext();

  const [result, setResult] = useState<PythonBlockResult>(undefined);
  const operation = getPythonOperation(cell.pipeline);
  const code = operation?.parameters.code ?? '';

  useEffect(() => {
    const { rows, fields } = operation?.parameters ?? {};
    if (result !== undefined || rows !== undefined || fields !== undefined) {
      setCell(setCellPipeline(cell, setPythonOperation(cell.pipeline, code, result)));
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, result]);

  const handleSetCode = (code: string) => {
    setCell(setCellPipeline(cell, setPythonOperation(cell.pipeline, code, result)));
  };

  const handleSetIsDragHovered = (value: boolean) => {
    props.onSetDraggable(value);
    setIsDragHovered(value);
  };

  const deleteAllowed = getNumberOfChildPipelines(exploration, cell.pipeline) === 0;

  return (
    <CollapsibleContainer
      className={container.cohortViewCell}
      onClick={(event) => shouldPropagateEvent(event) && props.onSelectCell?.()}>
      <div className={container.cellHeader}>
        <div className={container.cellControlsContainer}>
          <HideInEmbedded>
            {!isConversationPart && (
              <Icon
                name="DragHandle"
                size={10}
                className={container.dragHandle}
                onMouseOver={() => handleSetIsDragHovered(true)}
                onMouseOut={() => handleSetIsDragHovered(false)}
              />
            )}
          </HideInEmbedded>
          <MasterBadge exploration={exploration} pipeline={props.cell.pipeline} />
          <CellTitle
            exploration={exploration}
            value={cell.title ?? '(Untitled)'}
            onChange={(value) => setCell(setCellTitle(cell, value))}
          />
          <CellControls
            exploration={exploration}
            editButtonVisible={false}
            canDelete={deleteAllowed}
          />
          {isCollapsible && <CollapseButton />}
        </div>
      </div>
      <CollapsibleContent isDragHovered={isDragHovered}>
        <ErrorBoundary fallback={(errorData) => <GenericFallback {...errorData} />}>
          <PythonView
            cell={cell}
            setCell={setCell}
            code={code}
            setCode={handleSetCode}
            result={result}
            setResult={setResult}
          />
        </ErrorBoundary>
      </CollapsibleContent>
    </CollapsibleContainer>
  );
};

interface PythonViewProps {
  cell: PythonCell;
  setCell: (cell: PythonCell) => void;
  code: string;
  setCode: (code: string) => void;
  result: PythonBlockResult;
  setResult: (result: PythonBlockResult) => void;
}

const PythonView = (props: PythonViewProps) => {
  const { result, setCode, setResult, code: initialCode, cell } = props;
  const code = initialCode.trim();
  const [log, setLog] = useState<PythonLog[]>([]);
  const { getCellViewOption } = useExplorationCellContext();

  const fieldOrder = getCellViewOption('fieldOrder');

  const appendLog = (log: PythonLog) => setLog((logs) => [...logs, log]);
  const resetLog = () => setLog([]);

  return (
    <>
      <div className={container.cellSection}>
        <PythonInputView
          cellId={cell.id}
          code={code}
          setCode={setCode}
          resetLog={resetLog}
          appendLog={appendLog}
          setResult={setResult}
        />
      </div>
      {log.length > 0 && (
        <div className={container.cellSection}>
          <PythonLogView log={log} />
        </div>
      )}
      {result?.type === 'primitive' && (
        <div className={container.cellSection}>
          <PythonPrimitiveResponseView value={result.value} />
        </div>
      )}
      {result?.type === 'image' && (
        <div className={classNames(container.cellSection, style.imageSection)}>
          <img src={result.base64ImageString} className={style.imageOutput} />
        </div>
      )}
      {result?.type === 'html' && (
        <div className={classNames(container.cellSection, style.htmlSection)}>
          <InnerHTML html={result.htmlString} className={style.htmlOutput} />
        </div>
      )}
      {result?.type === 'table' && (
        <div className={classNames(container.cellSection, container.tableSection)}>
          <PythonResponseView
            records={result.rows}
            cell={props.cell}
            fetchMore={async () => {}}
            properties={sortProperties(result.fields, fieldOrder)}
            sort={[]}
            fieldOrder={fieldOrder ?? {}}
            grouping={[]}
            setCell={props.setCell}
            setSort={() => {}}
            loading={false}
            loaded
          />
        </div>
      )}
      {result === undefined && (
        <div className={editorStyle.empty}>
          <div className={editorStyle.text}>
            End your code block with an object or expression to see the results.
          </div>
        </div>
      )}
    </>
  );
};

interface PythonInputViewProps {
  cellId: string;
  code: string;
  setCode: (code: string) => void;
  resetLog: () => void;
  appendLog: (log: PythonLog) => void;
  setResult: (result: PythonBlockResult) => void;
  onRun?: () => void;
  onErrors?: (errors: string) => void;
}

const PythonInputView = (props: PythonInputViewProps) => {
  const {
    cellId,
    code: persistedCode,
    setCode: setPersistedCode,
    appendLog,
    resetLog,
    setResult,
  } = props;
  const [code, setCode] = useState(persistedCode);
  const [executing, setExecuting] = useState(false);

  const { account } = useAccountContext();
  const { exploration, getVariables } = useExplorationContext();
  const { models } = useMetadataContext();
  const trackEvent = useTrackEvent();

  useEffect(() => setCode(persistedCode), [persistedCode]);

  const client = useApolloClient();
  const loadPyData = async (
    title: string,
    page?: boolean,
    limit?: number,
    page_size?: number,
    columns?: string[],
  ): Promise<TableResult | undefined> => {
    try {
      appendLog({ type: 'out', string: `Starting to load data from "${title}"` });
      const dataSource = findDataSource(title, exploration, models, getVariables);

      const data = await loadDataByDataSource(
        dataSource,
        client,
        account.accountId,
        page,
        limit,
        page_size,
        columns,
      );
      appendLog({
        type: 'out',
        string: `Finished loading data: ${data?.rows?.length} rows, ${data?.fields?.length} columns`,
      });
      return data;
    } catch (error) {
      appendLog({ type: 'error', string: (error as Error).message });
    }
    return undefined;
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleRunPython = async () => {
    const { pyCode, pypiPackages } = parseCustomPackages(code);

    return await pyodideService.runExclusive(async () => {
      try {
        // Initialize Pyodide and ensure packages are loaded
        await pyodideService.ensurePackagesLoaded(pyCode, pypiPackages, appendLog);

        // Register the supersimple module with the current context
        await pyodideService.registerSupersimpleModule(cellId, loadPyData);

        // Execute the code with exploration context for isolation
        const executionResult = await pyodideService.executeCode(
          pyCode,
          { explorationId: exploration.explorationId, cellId },
          appendLog,
        );

        const figFlush = await pyodideService.flushMatplotlibFigures();

        const convertedPyResult = convertPythonResult(
          await pyodideService.initializePyodide(),
          executionResult.result,
          executionResult.namespace,
        );

        const { result: selectedResult, warnings } = selectPythonCellOutput(
          convertedPyResult,
          figFlush,
        );

        for (const warning of warnings) {
          appendLog({ type: 'out', string: `Warning: ${warning}` });
        }

        if (selectedResult === undefined && figFlush.figureCount === 0) {
          appendLog({
            type: 'out',
            string: 'Code did not end with an object or expression. No output to display.',
          });
        } else if (selectedResult === undefined && figFlush.figureCount > 0) {
          appendLog({
            type: 'error',
            string:
              'Figures were generated but could not be rendered. No output is available to display.',
          });
        }
        return selectedResult;
      } catch (error) {
        appendLog({
          type: 'error',
          string: `${error}`,
        });
      } finally {
        // Clean up the supersimple module registration
        await pyodideService.unregisterSupersimpleModule(cellId);
      }
    });
  };

  const handleSubmit = async () => {
    setPersistedCode(code);
    resetLog();
    setResult(undefined);

    if (code === '') {
      return;
    }

    trackEvent('Exploration Python Cell Run', {
      explorationId: exploration.explorationId,
      explorationName: exploration.name,
    });
    const start = new Date().getTime();

    try {
      setExecuting(true);
      const result = await handleRunPython();
      if (result !== undefined) {
        setResult(result);
      }

      trackEvent('Exploration Python Cell Run Success', {
        explorationId: exploration.explorationId,
        explorationName: exploration.name,
        durationMs: new Date().getTime() - start,
        resultType: result?.type,
      });
    } catch (error) {
      appendLog({
        type: 'error',
        string: `\n${(error as Error).message}\n${(error as Error).stack}\n`,
      });

      trackEvent('Exploration Python Cell Run Error', {
        explorationId: exploration.explorationId,
        explorationName: exploration.name,
        durationMs: new Date().getTime() - start,
        error,
      });
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className={editorStyle.inputContainer}>
      <CodeMirror
        value={code}
        onChange={setCode}
        onKeyDown={onKeyDown}
        extensions={[pythonLanguage(), lineNumbers(), tabIndentExtension]}
        className={classNames(editorStyle.codeInput)}
      />

      <div className={editorStyle.buttons}>
        <Button variant="primary" size="small" disabled={executing} onClick={handleSubmit}>
          Execute
        </Button>
      </div>
    </div>
  );
};

interface PythonLogViewProps {
  log: PythonLog[];
}

const PythonLogView = (props: PythonLogViewProps) => {
  return (
    <div className={style.logContainer}>
      {props.log.map((log, index) => (
        <div key={index} className={log.type === 'error' ? style.errorLog : style.regularLog}>
          {log.string}
        </div>
      ))}
    </div>
  );
};

interface PythonPrimitiveResponseViewProps {
  value: any;
}

const PythonPrimitiveResponseView = (props: PythonPrimitiveResponseViewProps) => {
  return <div className={style.logContainer}>{props.value}</div>;
};

interface PythonResponseViewProps {
  records: DataTableRow[];
  cell: PythonCell;
  setCell: (cell: PythonCell) => void;
  fetchMore: () => Promise<void>;
  properties: DataTableProperty[];
  sort: Sort;
  fieldOrder: Record<string, number>;
  grouping: string[];
  setSort: (sort: SortItem | null) => void;
  loaded: boolean;
  loading: boolean;
}

const createModelFromProperties = (properties: DataTableProperty[]): Model => ({
  modelId: 'python-results',
  name: 'Python',
  primaryKey: [],
  properties: properties.map((property) => ({
    ...property,
    type: property.type ?? 'String',
  })),
  relations: [],
  labels: {},
});

const PythonResponseView = (props: PythonResponseViewProps) => {
  const { cell } = useExplorationCellContext();
  const { isFirstLoad, isSubsequentLoad } = useLoadingStatus(props.loading, props.loaded);

  const handleReorderField = (fromIndex: number, toIndex: number, after: boolean) => {
    const fieldKey = props.properties.at(fromIndex)?.key;
    if (fieldKey === undefined) {
      throw new Error('Field key not found for reordering');
    }
    props.setCell(
      setViewOptions(props.cell, {
        ...props.cell.viewOptions,
        fieldOrder: setFieldIndex(fieldKey, fromIndex, toIndex, after, props.fieldOrder),
      }),
    );
  };

  return (
    <PaginatedRecords
      properties={props.properties}
      sort={props.sort}
      grouping={props.grouping}
      reorderField={handleReorderField}
      setSort={props.setSort}
      records={props.records}
      model={createModelFromProperties(props.properties)}
      fetchMore={props.fetchMore}
      footerContent={isSubsequentLoad && <Loader />}
      cellHeight={cell.viewOptions?.height}
      canEditPipeline={false}
      loading={isFirstLoad}
      component={HorizontalScrollTable}
    />
  );
};
